import {
  AlertCircle,
  Bluetooth,
  Calendar,
  CheckCircle,
  Code,
  Cpu,
  FileText,
  Globe,
  Layers,
  Layout,
  LifeBuoy,
  MapPin,
  PenTool,
  PlaySquare,
  RefreshCw,
  Search,
  Server,
  Shield,
  ShieldCheck,
  ShoppingBag,
  Smartphone,
  Target,
  TrendingUp,
  Users,
  Zap,
} from "lucide-react";

import type { AboutUsCopy } from "@components/bluetooth-landing/AboutUs";
import type { FAQCopy } from "@components/bluetooth-landing/FAQ";
import type { FooterCopy } from "@components/bluetooth-landing/Footer";
import type { HeroCopy } from "@components/bluetooth-landing/Hero";
import type { ProcessCopy } from "@components/bluetooth-landing/Process";
import type { ServicesCopy } from "@components/bluetooth-landing/Services";
import type { TargetAudienceCopy } from "@components/bluetooth-landing/TargetAudience";
import type { ValuePropositionCopy } from "@components/bluetooth-landing/ValueProposition";
import { faqsPl } from "@data/bluetooth-landing/faq";

export const title =
  "Aplikacje mobilne Bluetooth – firma tworząca aplikacje mobilne na zamówienie";
export const description =
  "Jesteśmy firmą tworzącą aplikacje mobilne dla firm z urządzeniami Bluetooth LE. Projektujemy i wdrażamy aplikacje mobilne na zamówienie dla iOS i Android – stabilne, gotowe na rynek europejski.";

export const structuredData = [
  {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "Blues Brackets",
    "url": "https://bluesbrackets.com",
    "logo": "https://bluesbrackets.com/favicon-dark.png",
    description,
    "areaServed": "EU",
    "contactPoint": {
      "@type": "ContactPoint",
      "contactType": "sales",
      "email": "hello@bluesbrackets.com",
      "availableLanguage": ["Polish", "English"],
    },
  },
  {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "Tworzenie aplikacji mobilnych Bluetooth na zamówienie",
    "provider": {
      "@type": "Organization",
      "name": "Blues Brackets",
    },
    description,
    "areaServed": "EU",
    "serviceType": "Mobile app development",
  },
  {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqsPl.map(faq => ({
      "@type": "Question",
      "name": faq.q,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.a,
      },
    })),
  },
];

export const servicesCopy: ServicesCopy = {
  header: {
    h2: "Usługi",
    h3: "Projektowanie aplikacji mobilnych – od Bluetooth po UX",
    p: "Od pierwszych szkiców i analiz, po wdrożenie i utrzymanie na produkcji. Prowadzimy Cię przez cały cykl życia produktu.",
  },
  services: [
    {
      Icon: PenTool,
      title: "Warsztaty projektowe",
      description:
        "Uzupełnimy i uporządkujemy Twoje wymagania w profesjonalny dokument, który posłuży jako podstawa procesu realizacji zamówienia. Zidentyfikujemy funkcjonalności i podzielimy projekt na etapy.",
    },
    {
      Icon: Search,
      title: "Analiza urządzeń i dokumentacji",
      description:
        "Jeśli dostawca nie może zapewnić pełnej dokumentacji, oferujemy analizę działania urządzenia. Poznajemy ograniczenia sprzętu i niwelujemy ryzyka przed rozpoczęciem projektu.",
    },
    {
      Icon: Layers,
      title: "Projekt UX/UI",
      description:
        "Projektujemy intuicyjny interfejs, który upraszcza obsługę urządzenia. Przygotowujemy makiety, dbając o wygodę użytkownika, spójność wizualną marki i gotowość do wdrożenia.",
    },
    {
      Icon: Smartphone,
      title: "Rozwój aplikacji iOS i Android",
      description:
        "Budujemy jedną aplikację wieloplatformową. Implementujemy komunikację z Bluetooth, logikę, konta i integrację z backendem. Testujemy scenariusze użycia na fizycznym sprzęcie.",
    },
    {
      Icon: Server,
      title: "Infrastruktura backendowa",
      description:
        "Budujemy usługi backendowe i integrujemy z infrastrukturą klienta. Zapewniamy panel administracyjny, monitoring, analitykę i system do zarządzania kontami.",
    },
    {
      Icon: PlaySquare,
      title: "Wdrożenie do sklepów",
      description:
        "Przygotowujemy aplikację do Apple App Store i Google Play. Pomagamy w kwestiach formalnych, konfigurujemy konta deweloperskie, kompletujemy opisy i polityki prywatności.",
    },
    {
      Icon: LifeBuoy,
      title: "Wsparcie po premierze",
      description:
        "Utrzymujemy aplikację, monitorujemy działanie i reagujemy na problemy. Zapewniamy aktualizacje pod nowe wersje iOS i Android oraz rozwijamy aplikację zgodnie z potrzebami.",
    },
  ],
  cta: {
    h4: "Potrzebujesz wsparcia?",
    p: "Opowiedz nam o swoim projekcie — dobierzemy zakres usług i przeprowadzimy Cię przez cały proces.",
    button: {
      text: "Umów darmową konsultację",
      href: "#kontakt",
    },
  },
};

export const heroCopy: HeroCopy = {
  header: "Aplikacje mobilne dla urządzeń",
  headerTail: "Bluetooth",
  description:
    "Tworzymy aplikacje mobilne dla firm z urządzeniami Bluetooth LE na zamówienie. Stabilne, intuicyjne i gotowe na europejski rynek – od prototypu po iOS i Android.",
  cta: {
    text: "Darmowa konsultacja",
    href: "#kontakt",
  },
  secondaryCta: {
    text: "Poznaj nasz proces",
    href: "#proces",
  },
  benefits: [
    { text: "Polski zespół", Icon: CheckCircle },
    { text: "Gotowe na RODO i rynek UE", Icon: CheckCircle },
    { text: "Wsparcie przed i po premierze", Icon: CheckCircle },
  ],
};

export const targetAudienceCopy: TargetAudienceCopy = {
  header: {
    h2: "Kim są nasi klienci?",
    h3: "Dla kogo jest ta oferta?",
    p: "Tworzymy aplikacje mobilne dla firm, które chcą stworzyć lub poprawić doświadczenia użytkowników związane ze sprzętem Bluetooth. Czy ta oferta jest dla Ciebie?",
  },
  points: [
    {
      Icon: Cpu,
      title: "Jesteś producentem lub importerem",
      description:
        "Urządzeń z technologią Bluetooth LE i szukasz zaufanego partnera do stworzenia oprogramowania.",
    },
    {
      Icon: Target,
      title: "Masz działające urządzenie",
      description:
        "Twój sprzęt jest gotowy, ale brakuje Ci dedykowanej aplikacji mobilnej, która pozwoli w pełni wykorzystać jego potencjał.",
    },
    {
      Icon: AlertCircle,
      title: "Twoja obecna aplikacja zawodzi",
      description:
        "Jest nieatrakcyjna wizualnie, niestabilna, gubi połączenie lub po prostu trudna w obsłudze dla końcowego użytkownika.",
    },
    {
      Icon: RefreshCw,
      title: "Brak pełnej dokumentacji",
      description:
        "Masz urządzenie wspierające Bluetooth LE, ale nie masz do niego dokumentacji. Potrafimy ją odtworzyć metodą inżynierii wstecznej.",
    },
    {
      Icon: Users,
      title: "Nowy kanał relacji",
      description:
        "Chcesz dodać aplikację jako nowoczesny kanał budowania długotrwałych relacji z klientem i zbierania cennej analityki.",
    },
  ],
  cta: {
    h4: "Brzmi znajomo?",
    text: "Porozmawiajmy o tym, jak możemy rozwiązać problemy w Twoim projekcie.",
    href: "#kontakt",
    buttonText: "Umów darmową konsultację",
  },
};

export const valuePropositionCopy: ValuePropositionCopy = {
  header: {
    h2: "Propozycja wartości",
    h3: "Dlaczego warto wybrać nas jako firmę tworzącą aplikacje mobilne Bluetooth?",
    p: "Jesteśmy zespołem, który łączy kompetencje w aplikacjach mobilnych, technologii bezprzewodowej i designie. Nie uczymy się na Twoim projekcie – wdrażamy sprawdzone praktyki.",
    imageAlt: "Tworzenie oprogramowania i kodu",
  },
  values: [
    {
      Icon: Bluetooth,
      title: "Bluetooth to nasza specjalność",
      description:
        "Mamy realne doświadczenie w pracy z urządzeniami Bluetooth Low Energy (BLE) oraz Auracast. Znamy ich możliwości i ograniczenia, wiemy jak testować komunikację oraz gdzie najczęściej pojawiają się problemy.",
    },
    {
      Icon: Shield,
      title: "Ekspert po Twojej stronie",
      description:
        "Wspieramy w technicznych rozmowach z producentem. Pomagamy skompletować lub odtworzyć dokumentację i doradzamy jak ograniczyć ryzyka już od samego początku projektu.",
    },
    {
      Icon: Layout,
      title: "UX stawiamy na pierwszym miejscu",
      description:
        "Doskonałe doświadczenie użytkownika to konieczny element udanej aplikacji. Nasz UX designer pomaga uprościć obsługę urządzenia i sprawić, że produkt jest przyjemny w codziennym użytkowaniu.",
    },
    {
      Icon: TrendingUp,
      title: "Aplikacja otwarta na rozwój",
      description:
        "Aplikacja to ekosystem otwarty na nowe produkty, analitykę i upselling. Poznajesz swoich klientów, budujesz ich lojalność, a aplikacja nadąża za Twoim rozwojem biznesowym.",
    },
    {
      Icon: Globe,
      title: "Spełniamy oczekiwania europejskich użytkowników",
      description:
        "Dbamy o jakość tłumaczeń, dostępność, bezpieczeństwo danych i zgodność z RODO. Wszystkie dane użytkowników są przechowywane w UE pod okiem polskich specjalistów.",
    },
  ],
};

export const processCopy: ProcessCopy = {
  header: {
    h2: "Nasz Proces",
    h3: "Poznaj proces powstawania aplikacji",
    p: "Przejrzyste zasady współpracy, zdefiniowane etapy i ciągła komunikacja. Zobacz jak działamy od pierwszego maila do wdrożenia.",
  },
  stepNumber: "Krok",
  steps: [
    {
      Icon: Calendar,
      title: "Darmowe spotkanie online",
      duration: "w kilka godzin",
      description:
        "Otrzymujesz 30 minut rozmowy z ekspertem oraz podsumowanie ze wstępną wyceną projektu.",
    },
    {
      Icon: FileText,
      title: "Warsztaty produktowe",
      duration: "w kilka dni",
      description:
        "2h warsztatów online, po których otrzymujesz raport PDF, szczegółową wycenę i ofertę realizacji.",
    },
    {
      Icon: Search,
      title: "Analiza próbki sprzętu",
      duration: "1-2 tygodnie",
      description:
        "Dokumentacja wyników badania komunikacji i pewność, że urządzenie spełnia Twoje oczekiwania i pozwoli zrealizować projekt.",
    },
    {
      Icon: PenTool,
      title: "Podpisanie umowy",
      duration: "ustalenie harmonogramu",
      description:
        "Ustalamy termin rozpoczęcia i zakończenia realizacji projektu z gwarancją terminowości.",
    },
    {
      Icon: Code,
      title: "Etapowa realizacja",
      duration: "min. 2-3 miesiące",
      description:
        "Makiety, programowanie, testy. Twoja aplikacja nabiera kształtów, a Ty obserwujesz jak powstaje, testując wersje demo.",
    },
    {
      Icon: ShoppingBag,
      title: "Wdrożenie do sklepów",
      duration: "min. 2 tygodnie",
      description:
        "Przeprowadzamy proces zatwierdzenia przez Apple i Google. Publikujemy aplikację w App Store i Google Play.",
    },
    {
      Icon: CheckCircle,
      title: "Przekazanie i wsparcie",
      duration: "na stałe",
      description:
        "Otrzymujesz kod źródłowy lub rozpoczynamy usługę ciągłego wsparcia, hostingu i utrzymania.",
    },
    {
      Icon: Zap,
      title: "Dalszy rozwój",
      duration: "kolejne wersje",
      description:
        "Aplikacja jest gotowa na rozwój, dodanie analityki, nowych funkcji marketingowych i kolejnych produktów.",
    },
  ],
  cta: {
    href: "#kontakt",
    text: "Zamów aplikację mobilną na zamówienie",
  },
};

export const aboutUsCopy: AboutUsCopy = {
  header: {
    h2: "O nas",
    h3: "Lokalny zespół, europejskie standardy",
    p1: "Jesteśmy małym zespołem z Polski, który łączy doświadczenie w aplikacjach mobilnych, Bluetooth i systemach embedded.",
    p2: "Michał, Tomasz i Kuba dbają o implementację i stabilność kodu po wdrożeniu, a Mateusz projektuje UX, który ułatwia codzienne korzystanie z produktu.",
    p3: "Zgodność z RODO",
    p4: "Dane w UE",
    Icon: ShieldCheck,
    imageAlt: "Zespół pracujący nad aplikacją w biurze",
  },
  values: [
    {
      Icon: Users,
      title: "Pracujesz bezpośrednio z nami",
      description:
        "Otrzymujesz partnera technicznego, bez zbędnych pośredników.",
    },
    {
      Icon: MapPin,
      title: "Rozumiemy wymagania europejskiego rynku",
      description:
        "Wiemy jak wdrożyć produkt na rynek UE i spełnić wymagania RODO.",
    },
    {
      Icon: ShieldCheck,
      title: "Dbamy o bezpieczeństwo danych",
      description:
        "Wszystkie dane użytkowników są przechowywane w UE pod okiem polskich specjalistów.",
    },
  ],
  cta: {
    text: "Napisz do nas",
    href: "#kontakt",
  },
};

export const faqCopy: FAQCopy = {
  header: {
    h2: "FAQ",
    h3: "Najczęściej zadawane pytania",
  },
  faqs: faqsPl,
};

export const footerCopy: FooterCopy = {
  header: {
    h2: "Szukasz partnera technologicznego?",
    p: "Świetnie trafiłeś! Umów się na darmową konsultację, opowiedz nam o swoim urządzeniu i poznaj wstępną wycenę Twojego projektu.",
  },
  cta: {
    href: "#kontakt",
    text: "Umów darmową konsultację",
    p: "Jesteśmy firmą tworzącą aplikacje mobilne dla firm z urządzeniami Bluetooth LE. Zapewniamy kompleksowe wsparcie od koncepcji po wdrożenie i utrzymanie.",
    email: "hello@bluesbrackets.com",
    phone: "+48 535 462 678",
  },
  links: {
    h4: "Na skróty",
    links: [
      { href: "/projects", text: "Projekty" },
      { href: "/blog", text: "Blog" },
      { href: "#uslugi", text: "Nasze usługi" },
      { href: "#proces", text: "Proces współpracy" },
      { href: "#faq", text: "FAQ" },
    ],
  },
  knowledgeBase: {
    h4: "Baza wiedzy",
    links: [
      {
        href: "/pl/blogs/is_bluetooth_le_right_for_your_product",
        text: "Czy Bluetooth LE to właściwa technologia do realizacji Twojego produktu lub MVP?",
      },
      {
        href: "/pl/blogs/bluetooth_le_product_architecture",
        text: "Architektura produktu opartego o Bluetooth LE",
      },
      {
        href: "/pl/blogs/ble_mobile_application_support",
        text: "Wsparcie BLE po stronie aplikacji mobilnej",
      },
      {
        href: "/pl/blogs/ble_embedded_development_platforms",
        text: "Najlepsze platformy rozwojowe embedded z Bluetooth LE",
      },
      {
        href: "/pl/blogs/auracast_mcu",
        text: "ESP32 Auracast: czy działa? Najlepsze MCU do LE Audio",
      },
      {
        href: "/pl/blogs/ble_hardware_2026",
        text: "Najlepszy hardware do rozwoju Bluetooth Low Energy BLE w 2026 roku",
      },
    ],
  },
  copyright: "bluesBrackets. Wszelkie prawa zastrzeżone.",
  privacyPolicy: "Polityka prywatności",
  privacyPolicyHref: "/policy",
  contactForm: {
    heading: "Napisz do nas",
    fields: {
      name: {
        label: "Imię i nazwisko",
        placeholder: "Jan Kowalski",
        requiredError: "Podaj imię i nazwisko",
      },
      email: {
        label: "Adres e-mail",
        placeholder: "jan@firma.pl",
        requiredError: "Podaj adres e-mail",
        invalidError: "Podaj poprawny adres e-mail",
      },
      topic: {
        label: "Opis projektu",
        placeholder: "Krótko opisz swój projekt i wyzwania...",
        requiredError: "Opisz swój projekt",
        maxLengthError: "Opis może mieć maksymalnie 2000 znaków",
      },
    },
    submit: "Wyślij zapytanie",
    submitting: "Wysyłanie...",
    successMessage: "Wiadomość wysłana!",
    errorMessage: "Nie udało się wysłać wiadomości :(",
    responseTime: "Odpowiemy w ciągu 24 godzin w dni robocze.",
    emailSubjectPrefix: "Imię i nazwisko",
  },
};
